import { GameServices } from "game/GameServices";
import { View } from "@westpierstudio/luma/mvc";
import { Tween } from "@westpierstudio/luma/createjs";
import { PayRow } from "servertypes/gameevents/OpenGame";
import { WinDisplayConfig } from "./WinlinesModel";
import { ReelsLayout } from "components/reels/ReelsModel";
import { PayLineWin, SymbolPos } from "servertypes/SharedTypes";
import { getRectTexture } from "utils/Misc";
import { Signal } from "type-signals";
import { times } from "lodash";
import { BoundedContainer, ImplementBoundedContainer } from "utils/BoundedContainer";
import { AdvancedText } from "@westpierstudio/luma/utils/advancedtext";
import { Container, Sprite, Text, SCALE_MODES, NineSlicePlane } from "pixi.js";

class WinLineSignals {
    winLineComplete = new Signal();
}

export class WinLines extends View {
    private static spriteMaskMap = new Map<string, Sprite>;
    private readonly scatterWinBoxIndex = this.services.resources.getSetting(`winlines`).scatterWinBoxIndex;
    private spec!: Record<string, any>;
    private betlines!: {indices: number[], sprite: Sprite}[];
    private winBoxContainers!: Container[][];
    private winBoxTabs!: NineSlicePlane[][];
    private reelsLayout!: ReelsLayout;
    private winDisplayConfig!: WinDisplayConfig;
    private haveSetup = false;
    private lineValue!: Text | AdvancedText;
    signals = new WinLineSignals();
    valueBounds!: BoundedContainer;

    constructor(protected override readonly services: GameServices) {
        super(services);
    }

    setup(paylines: PayRow[][], reelsLayout: ReelsLayout, specKey: string): void {

        if (!this.haveSetup) {

            const {factory, resources} = this.services;

            this.spec = resources.getSpec(`winlines`);
            factory.applySpec(this, this.spec[specKey]);

            this.setupBetLines(paylines);
            this.setupWinBoxes(reelsLayout, paylines.length);

            this.lineValue = factory.createText(``, this.spec.lineValue);

            this.reelsLayout = reelsLayout;
            this.haveSetup = true;

            this.valueBounds = ImplementBoundedContainer(this.lineValue, this.spec.valueBounds);
        }
    }

    setWinDisplayConfig(config: WinDisplayConfig): void {
        this.winDisplayConfig = config;
    }

    showWinLine(win: PayLineWin, timeBetweenWinsActive: boolean): void {

        const {paylineIndex, pattern} = win;
        const {setPositions, symbolSize} = this.reelsLayout;
        const {winBoxLineWidth} = this.spec;
        const winBoxTabSpec = this.spec.winBoxTabValue;

        const indices = this.betlines[paylineIndex].indices;
        let symbolIndex, reelIndex, winBox;

        for (const value of pattern) {

            reelIndex = value.column;
            symbolIndex = value.row;

            winBox = this.winBoxContainers[reelIndex][win.paylineIndex];
            winBox.y = setPositions[reelIndex].y + (symbolIndex * symbolSize.height) - winBoxLineWidth/2;
            winBox.visible = true;

            if (reelIndex === 0) {

                const value = win.moneyWon;
                const maxWidth = this.winBoxTabs[reelIndex][win.paylineIndex].width - this.spec.lineValueMinimumPadding;

                this.lineValue.scale.set(1, 1);

                this.lineValue.text = this.services.currency.formatCoins(
                    value,
                    {useSubUnits: true, group: true, addTrailingZeros: true, trimIntegers: false}
                );
                this.services.factory.applySpec(this.lineValue, this.spec.lineValue);
                this.services.factory.applySpec(this.winBoxTabs[reelIndex][win.paylineIndex], winBoxTabSpec);

                if (this.lineValue.width > maxWidth) {

                    const ratio = maxWidth/this.lineValue.width;
                    this.lineValue.scale.set(ratio, ratio);
                }

                this.valueBounds = ImplementBoundedContainer(this.lineValue, this.spec.valueBounds);
                this.winBoxTabs[reelIndex][win.paylineIndex].addChild(this.valueBounds);
                this.winBoxTabs[reelIndex][win.paylineIndex].visible = true;
                this.valueBounds.update();
            } else {

                this.winBoxTabs[reelIndex][win.paylineIndex].visible = false;
            }
        }

        const betlineSprite =  this.betlines[paylineIndex].sprite;
        this.setbetlineMask(win, indices, betlineSprite);
        betlineSprite.visible = true;

        Tween.removeTweens(this);
        const tween = Tween.get(this);

        tween.wait(this.winDisplayConfig.displayWinTime);
        tween.call(() => {
            this.hideAllWinBoxes();
            this.hideAllWinLines();
        });

        if (timeBetweenWinsActive) {
            tween.wait(this.winDisplayConfig.timeBetweenWins);
        }

        tween.call(() => {
            this.signals.winLineComplete.dispatch();
        });
    }

    showScatterWin(symbols: SymbolPos[], bonusComplete: boolean = false, bonusWinnings?: number, loopIndex?: number, skipSignal?: boolean): void {

        const {winBoxLineWidth} = this.spec;
        const {setPositions, symbolSize} = this.reelsLayout;
        const winBoxTabSpec = loopIndex ? this.spec.winBoxTabMessage: this.spec.winBoxTabValue;

        let minReelIndex = Infinity;
        for (const value of symbols) {

            if (value.column < minReelIndex)
            {

                minReelIndex = value.column;
            }
        }

        let winBox;
        let winBoxTab;

        for (const value of symbols) {

            winBox = this.winBoxContainers[value.column][this.scatterWinBoxIndex];
            winBox.y = setPositions[value.column].y + (value.row * symbolSize.height) - winBoxLineWidth/2,
            winBox.visible = true;

            winBoxTab = this.winBoxTabs[value.column][this.scatterWinBoxIndex];
            this.services.factory.applySpec(winBoxTab, winBoxTabSpec);
            winBoxTab.visible = false;

            if (value.column === minReelIndex) {

                if (bonusComplete && bonusWinnings) {

                    const winningsValue = bonusWinnings;
                    const maxWidth = this.winBoxTabs[value.column][this.scatterWinBoxIndex].width - this.spec.lineValueMinimumPadding;

                    this.lineValue.scale.set(1, 1);

                    if (loopIndex) {

                        this.lineValue.text = this.services.resources.getMessage(`winBoxTabBonusComplete`);
                        this.services.factory.applySpec(this.lineValue, this.spec.completeText);
                    } else {

                        this.lineValue.text = this.services.currency.formatCoins(
                            winningsValue,
                            {useSubUnits: true, group: true, addTrailingZeros: true, trimIntegers: false}
                        );
                        this.services.factory.applySpec(this.lineValue, this.spec.lineValue);
                    }

                    if (this.lineValue.width > maxWidth) {

                        const ratio = maxWidth/this.lineValue.width;
                        this.lineValue.scale.set(ratio, ratio);
                    }

                    this.valueBounds = ImplementBoundedContainer(this.lineValue, this.spec.valueBounds);
                    winBoxTab.addChild(this.valueBounds);
                    winBoxTab.visible = true;
                    this.valueBounds.update();
                } else {

                    winBoxTab.visible = false;
                }
            }
        }

        Tween.removeTweens(this);
        const tween = Tween.get(this);

        tween.wait(this.winDisplayConfig.displayWinTime);
        tween.call(() => {
            this.hideAllWinBoxes();
        });
        tween.wait(this.winDisplayConfig.timeBetweenWins);
        tween.call(() => {
            if (bonusComplete && bonusWinnings && loopIndex !== 1) {
                this.showScatterWin(symbols, bonusComplete, bonusWinnings, 1, skipSignal);
            } else {
                if (!skipSignal) {
                    this.signals.winLineComplete.dispatch();
                }
            }
        });
    }

    hideAllWinLines(): void {

        for (const entry of Object.values(this.betlines)) {
            entry.sprite.visible = false;
        }
    }

    showAllWinBoxes(): void {

        for (const set of this.winBoxContainers) {
            for (const box of set) {

                if (box) {
                    box.visible = true;
                }
            }
        }
    }

    hideAllWinBoxes(): void {

        for (const set of this.winBoxContainers) {
            for (const box of set) {

                if (box) {
                    box.visible = false;
                }
            }
        }
    }

    override reset(): void {

        if (this.haveSetup) {

            Tween.removeTweens(this);

            this.hideAllWinBoxes();
            this.hideAllWinLines();
        }
    }

    private setupBetLines(paylines: PayRow[][]): void {

        this.betlines = [];
        let sprite;

        for (const [i, entry] of paylines.entries()) {

            sprite = this.services.factory.createSprite(this.spec.slices[`line${(i+1)}`], this);
            sprite.visible = false;
            this.betlines.push({indices: entry, sprite});
        }
    }

    private setupWinBoxes(reelsLayout: ReelsLayout, paylineCount: number): void {

        const {setPositions, reelsPerSet} = reelsLayout;
        const {factory} = this.services;

        this.winBoxContainers = times(reelsLayout.reelsPerSet, () => []);
        this.winBoxTabs = times(reelsLayout.reelsPerSet, () => []);

        for (let i=0; i<reelsPerSet; i++) {

            for (let j=0; j<paylineCount; j++) {

                this.winBoxContainers[i][j] = factory.createContainer(
                    {
                        x: setPositions[i].x,
                        y: setPositions[i].y,
                        anchor: 0.5,
                        visible: false
                    },
                    this
                );

                factory.createSpriteFromTextureKey(
                    `box${(j+1)}`,
                    {anchor: 0.5},
                    this.winBoxContainers[i][j]
                );

                const texture = this.services.resources.getTexture(`winboxvalue${(j+1)}`);
                const {left, right, top, bottom} = this.spec.winBoxTabSlice;
                const winBoxTab = new NineSlicePlane(texture, left, top, right, bottom);

                factory.applySpecAndParent(winBoxTab, this.spec.winBoxTabValue, this.winBoxContainers[i][j], 0);
                this.winBoxTabs[i][j] = winBoxTab;
            }
        }
    }

    /*
        Sprite Mask:
            Pixels discarded based on red channel
            Black squares representing inside of win boxes drawn into larger white rectangle
    */
    setbetlineMask(win: PayLineWin, indices: number[], sprite: Sprite): void {

        sprite.removeChildren();

        const key = win.pattern.map((item) => {
            return `${item.column}${item.row}`;
        }).toString();

        const {winBoxLineWidth} = this.spec;
        const {symbolSize} = this.reelsLayout;

        let maskSprite;

        if (WinLines.spriteMaskMap.has(key)) {
            maskSprite = WinLines.spriteMaskMap.get(key) as Sprite;
        } else {

            const {application, factory} = this.services;
            const {winBoxHeight, winBoxWidth} = this.spec;
            const {setPositions, reelsPerSet, reelSpacing, symbolCount} = this.reelsLayout;

            const betlineOverhang = 6; //Should be evenly divisible by 2
            const maskWidth =  reelsPerSet * symbolSize.width + (reelsPerSet-1) * reelSpacing + betlineOverhang;
            const maskHeight = symbolCount * symbolSize.height;
            const textureScale = 1;

            const maskContainer = factory.createContainer({});
            let reelIndex, symbolIndex, x, y;

            factory.createSpriteFromTexture(
                getRectTexture(this.services, maskWidth, maskHeight, 0xFFFFFF, textureScale),
                {},
                maskContainer
            );

            for (const value of win.pattern) {

                reelIndex = value.column;
                symbolIndex = indices[reelIndex];

                x = setPositions[reelIndex].x - symbolSize.height/2 + betlineOverhang/2;
                y = symbolSize.height * symbolIndex;

                factory.createSpriteFromTexture(
                    getRectTexture(this.services, winBoxWidth, winBoxHeight, 0x000000, textureScale),
                    {x, y},
                    maskContainer
                );
            }

            const maskTexture = application.renderer.generateTexture(maskContainer, SCALE_MODES.NEAREST, textureScale);
            maskSprite = factory.createSpriteFromTexture(maskTexture, {x: -betlineOverhang/2, y: 0});

            WinLines.spriteMaskMap.set(key, maskSprite);
        }

        maskSprite.y = 0;
        sprite.addChild(maskSprite);
        maskSprite.y -= sprite.y + winBoxLineWidth/2 - symbolSize.height/2;
        sprite.mask = maskSprite;
    }
}